// assets/tmf-upsell-pdp.js - TMF · upsell under Add to cart (blocks/tmf-upsell-pdp.liquid). 24/10/2026.
// Reads the live design from the TMF · Personalize block of the same section (event "tmf:change", element.widget):
//   - add-ons (never pre-ticked): added right after the main product is added to cart (product form event)
//   - hoodie upgrade card: selects the Hoodie value of the Style option (preview switches with it)
//   - "Same design, more gifts": every upsell product with a photo (mug, glass, ornament, canvas, blanket, speaker, phone),
//     drawn live with the shopper's OWN design (same layout + _cfg; ink / mug color only for contrast), size picker in
//     the card, "+ Add" adds the same _cfg + _kind (26/10)
//   - "Edit design" from the cart (?cfg=...&tmf_line=<key>): after Add to cart, the old cart line is removed
//   - 26/10: the personalizer's "Preview my design" sheet fires "tmf:sheet" {open, slot}: while it is open the
//     "Same design, more gifts" box moves into the sheet (right under the design summary), then back here
// Products that are not set up: no button ("Coming soon" only when the block says so). Never throws on missing data.
import { StandardEvents } from '@shopify/events';
import { getJson, paintDesign, money, cartAdd, cartChange, getCart } from './tmf-live.js';

const ADDON = {
  box: { label: 'Gift box', sub: 'Kraft box + tissue, ready to give.' },
  note: { label: 'Gift note card', sub: 'Printed card with your message, no prices inside.' },
  priority: { label: 'Priority production', sub: '' },
};

/** @param {string} tag @param {Record<string, any>} [attrs] @param {(Node|string|null)[]} [kids] */
function el(tag, attrs = {}, kids = []) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'text') e.textContent = v;
    else if (k === 'cls') e.className = v;
    else e.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of kids) if (c != null) e.append(typeof c === 'string' ? document.createTextNode(c) : c);
  return e;
}
const clone = (/** @type {any} */ o) => JSON.parse(JSON.stringify(o));

class TmfUpsellPdp extends HTMLElement {
  /** @type {any} */ conf = {};
  /** @type {any} */ widget = null;
  /** @type {any} */ db = null;
  /** @type {any} */ media = null;
  mediaBase = '';
  #frame = 0;
  #busy = false;
  /** @type {Element | Document} */
  #section = document;
  /** @type {string | null} */
  #editKey = null;
  /** @type {string | null} _cfg JSON of the cart line being edited (line keys can change when the cart changes) */
  #editCfg = null;
  #editTries = 0;
  /** @type {HTMLElement | null} "Same design" box (kept as a reference: it moves into the preview sheet and back) */
  #same = null;
  /** @type {Comment | null} where the box lives on the page */
  #sameHome = null;

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    try {
      this.conf = JSON.parse(this.querySelector('[data-tmf-upsell-config]')?.textContent || '{}');
    } catch {
      this.conf = {};
    }
    this.#section = this.closest('.shopify-section') || document;
    this.mediaBase = String(this.conf.mediaUrl || '').split('?')[0].replace(/[^/]*$/, '');
    this.#same = this.querySelector('[data-same]');
    this.#section.addEventListener('tmf:change', this.#onDesign);
    this.#section.addEventListener('tmf:sheet', this.#onSheet);
    this.#section.addEventListener(StandardEvents.cartLinesUpdate, this.#onCart);
    window.addEventListener('resize', this.#schedule);
    this.#buildAddons();
    this.#readEditLine();
    Promise.all([getJson(this.conf.designsUrl), getJson(this.conf.mediaUrl)]).then(([db, media]) => {
      this.db = db;
      this.media = media;
      this.#schedule();
    });
    const tp = /** @type {any} */ (this.#section.querySelector('tmf-personalize'));
    if (tp?.widget?.state) {
      this.widget = tp.widget;
      this.#schedule();
    }
  }

  disconnectedCallback() {
    this.#section.removeEventListener('tmf:change', this.#onDesign);
    this.#section.removeEventListener('tmf:sheet', this.#onSheet);
    this.#section.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCart);
    window.removeEventListener('resize', this.#schedule);
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);
    this.#io?.disconnect();
    this.#io = null;
    this.#sheetBack();
  }

  /** Preview sheet opened / closed: move the "Same design" box into the sheet slot, and back. @param {Event} e */
  #onSheet = (e) => {
    const d = /** @type {any} */ (e).detail || {};
    const box = this.#same;
    if (!box) return;
    if (d.open && d.slot instanceof HTMLElement) {
      if (!this.#sameHome) {
        this.#sameHome = document.createComment('tmf-same');
        box.before(this.#sameHome);
      }
      d.slot.append(box);
      this.#schedule();
    } else {
      this.#sheetBack();
    }
  };

  #sheetBack() {
    const box = this.#same;
    if (box && this.#sameHome && this.#sameHome.parentNode) {
      this.#sameHome.replaceWith(box);
      this.#sameHome = null;
      this.#schedule();
    }
  }

  /** @param {Event} e */
  #onDesign = (e) => {
    const t = /** @type {any} */ (e.target);
    const w = t?.widget || t?.closest?.('tmf-personalize')?.widget;
    if (!w) return;
    this.widget = w;
    this.#schedule();
  };

  /** 26/10 (speed: every keystroke re-planned 7 word-cloud tiles + the hoodie card, 0.4-1.3 s on a 4x slower CPU):
   *  draw only when the block (or the Same design box inside the preview sheet) is near the screen, at most once per 150 ms
   *  while typing; otherwise remember it is stale and draw when it scrolls in. */
  #dirty = false;
  #timer = 0;
  #drawn = false;
  /** @type {IntersectionObserver | null} */
  #io = null;
  #near() {
    if (this.#same?.closest('dialog[open]')) return true; // box moved into the open preview sheet
    const r = this.getBoundingClientRect();
    if (!r.width && !r.height) return true; // not laid out (unknown position): draw as before
    return r.bottom > -300 && r.top < innerHeight + 300;
  }
  #watch() {
    if (this.#io || typeof IntersectionObserver !== 'function') return;
    this.#io = new IntersectionObserver((es) => {
      if (this.#dirty && es.some((x) => x.isIntersecting)) this.#schedule();
    }, { rootMargin: '300px 0px' });
    this.#io.observe(this);
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);
    if (typeof IntersectionObserver === 'function' && !this.#near()) {
      this.#dirty = true;
      this.#watch();
      return;
    }
    const go = () => {
      this.#frame = requestAnimationFrame(() => {
        this.#dirty = false;
        try {
          this.#render();
          this.#drawn = true;
        } catch (error) {
          console.warn('[tmf-upsell] render', error);
        }
      });
    };
    if (this.#drawn) this.#timer = window.setTimeout(go, 150);
    else go();
  };

  /** Shopper's current design: valid _cfg, or the preview (sample name) while they are still typing. */
  #current() {
    const w = this.widget;
    if (!w || !w.state || !w.res || !w.lib) return null;
    const valid = !!w.valid;
    const cfg = valid ? clone(w.res.cfg) : w.previewCfg();
    if (!valid && cfg.items) {
      const it = cfg.items.filter(Boolean);
      if (it.length && cfg.count > it.length && w.spec.items === 'per') cfg.count = it.length;
      cfg.items = it;
    }
    if (!cfg.ink) cfg.ink = w.state.ink || 'white';
    return { valid, cfg, design: w.design, lib: w.lib, family: w.family || 'apparel', colorName: w.colorName, hex: w.hex, style: w.styleName };
  }

  /** @param {any} src @param {string} fam @param {string} [variantTitle] */
  #cross(src, fam, variantTitle) {
    const U = /** @type {any} */ (window).TMFUpsell;
    return U.crossCfg(src.cfg, src.design, fam, { lib: src.lib, db: this.db, tiles: this.media?.tiles, bgHex: this.media?.bgHex?.[fam], variantTitle });
  }

  #render() {
    const src = this.#current();
    if (!src || !(/** @type {any} */ (window).TMFUpsell)) return;
    this.#renderSame(src);
    this.#renderHoodie(src);
  }

  /* ---------- Same design, more gifts (26/10: every upsell product, always the shopper's own design) ---------- */
  /** @type {Record<string, string>} chosen option per card (variant id, or mug size) */
  #pick = {};

  /** Variants offered in a card (glass: full color print; ornament: crystal circle = the photo). @param {string} fam */
  #offered(fam) {
    const vs = /** @type {any[]} */ (this.conf.cross?.[fam]?.variants || []).filter((v) => v.available);
    let out = vs;
    if (fam === 'glass') out = vs.filter((v) => !/engrav/i.test(v.title));
    if (fam === 'ornament') out = vs.filter((v) => /circle|crystal|prism/i.test(v.title));
    return out.length ? out : vs;
  }

  /** {variant, choices:[{value,label}], value, needsChoice, all} for a card. Mug: color follows the ink, shopper picks the size.
   * @param {string} fam @param {any} src */
  #variantFor(fam, src) {
    const vs = this.#offered(fam);
    if (fam === 'mug') {
      const x = this.#cross(src, fam);
      const col = x.tile === 'mug-white' ? /white/i : /black/i;
      const same = vs.filter((v) => col.test(v.title));
      const mv = same.length ? same : vs;
      const size = (/** @type {any} */ v) => String(v.title).split(' / ').pop() || v.title;
      const choices = mv.map((v) => ({ value: size(v), label: size(v) }));
      const want = this.#pick.mug || choices[0]?.value;
      const variant = mv.find((v) => size(v) === want) || mv[0] || null;
      return { variant, choices: choices.length > 1 ? choices : [], value: variant ? size(variant) : '', needsChoice: false, all: mv };
    }
    const choices = vs.length > 1 ? vs.map((v) => ({ value: String(v.id), label: v.title })) : [];
    const needsChoice = fam === 'phone' && vs.length > 1;
    let variant = vs.find((v) => String(v.id) === this.#pick[fam]) || null;
    if (!variant && !needsChoice) variant = vs.slice().sort((a, b) => a.price - b.price)[0] || null;
    return { variant, choices, value: variant ? String(variant.id) : '', needsChoice, all: vs };
  }

  /** Desktop grid: the first card spans the leftover cells so no row ends with a lone card. Small screens: carousel.
   * @param {HTMLElement} grid */
  #layoutGrid(grid) {
    const tiles = /** @type {HTMLElement[]} */ (Array.from(grid.querySelectorAll('.tmf-u__tile')));
    for (const t of tiles) {
      t.classList.remove('is-wide');
      t.style.removeProperty('--span');
    }
    const cs = getComputedStyle(grid);
    if (cs.overflowX === 'auto' || cs.overflowX === 'scroll') return; // carousel
    const cols = cs.gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    const r = tiles.length % cols;
    if (r && tiles.length > cols && tiles[0]) {
      tiles[0].classList.add('is-wide');
      tiles[0].style.setProperty('--span', String(cols - r + 1));
    }
  }

  /** @param {any} src */
  #renderSame(src) {
    const box = this.#same;
    const grid = /** @type {HTMLElement | null} */ (box?.querySelector('[data-grid]') || null);
    if (!box || !grid) return;
    if (this.conf.showSame === false || !this.media) {
      box.hidden = true;
      return;
    }
    const U = /** @type {any} */ (window).TMFUpsell;
    const cross = this.conf.cross || {};
    const tiles = this.media.tiles || {};
    const fams = /** @type {string[]} */ (U.FAMS || ['mug', 'ornament', 'canvas']).filter((f) => f !== src.family && cross[f] && this.#offered(f).length && tiles[f]);
    if (!fams.length) {
      box.hidden = true;
      return;
    }
    box.hidden = false;
    const name = (/** @type {string} */ f) => (U.NAMES && U.NAMES[f]) || f;
    if (grid.getAttribute('data-fams') !== fams.join()) {
      grid.setAttribute('data-fams', fams.join());
      grid.innerHTML = '';
      for (const f of fams) {
        const cv = el('canvas', { role: 'img', 'aria-label': `${name(f)} preview with your design` });
        const sel = /** @type {HTMLSelectElement} */ (el('select', { cls: 'tmf-u__opt', 'data-opt': f, 'aria-label': f === 'phone' ? 'Phone model' : `${name(f)} size`, hidden: true }));
        sel.addEventListener('change', () => {
          this.#pick[f] = sel.value;
          sel.removeAttribute('aria-invalid');
          this.#schedule();
        });
        const add = /** @type {HTMLButtonElement} */ (el('button', { type: 'button', cls: 'tmf-u__add', 'data-add': f, text: '+ Add' }));
        add.addEventListener('click', () => this.#addCross(f, add));
        grid.append(
          el('div', { cls: 'tmf-u__tile', role: 'listitem', 'data-fam': f }, [
            el('div', { cls: 'tmf-u__img' }, [cv]),
            el('div', { cls: 'tmf-u__meta' }, [el('span', { cls: 'tmf-u__name', text: name(f) }), el('span', { cls: 'tmf-u__price', 'data-price': f }), sel, add]),
          ])
        );
      }
    }
    this.#layoutGrid(grid);
    const fmt = this.conf.moneyFormat;
    for (const t of /** @type {NodeListOf<HTMLElement>} */ (grid.querySelectorAll('.tmf-u__tile'))) {
      const f = t.getAttribute('data-fam') || '';
      let vf, x;
      try {
        vf = this.#variantFor(f, src);
        x = this.#cross(src, f, f === 'mug' ? undefined : vf.variant?.title);
      } catch {
        continue;
      }
      const cv = /** @type {HTMLCanvasElement} */ (t.querySelector('canvas'));
      paintDesign(cv, cv.clientWidth || 160, { kind: f, tile: x.tile, design: x.design, cfg: x.cfg, lib: src.lib, media: this.media, mediaBase: this.mediaBase, onReady: this.#schedule });
      const sel = /** @type {HTMLSelectElement} */ (t.querySelector('[data-opt]'));
      const sig = vf.choices.map((c) => c.value).join('|') + (vf.needsChoice ? '|?' : '');
      if (sel.dataset.sig !== sig) {
        sel.dataset.sig = sig;
        sel.innerHTML = '';
        if (vf.needsChoice) sel.append(el('option', { value: '', text: 'Choose model' }));
        for (const c of vf.choices) sel.append(el('option', { value: c.value, text: c.label }));
      }
      sel.hidden = !vf.choices.length;
      if (vf.choices.length) sel.value = vf.value;
      // price of the selected variant; "From" while it is the cheapest of several prices
      const prices = vf.all.map((/** @type {any} */ v) => v.price);
      const min = Math.min(...prices);
      const shown = vf.variant ? vf.variant.price : min;
      const pe = t.querySelector('[data-price]');
      if (pe) pe.textContent = (new Set(prices).size > 1 && shown === min ? 'From ' : '') + money(shown, fmt);
      const b = t.querySelector('[data-add]');
      if (b) {
        b.setAttribute('aria-label', `Add ${name(f)} with your design, ${money(shown, fmt)}`);
        if (src.valid) b.removeAttribute('aria-disabled');
        else b.setAttribute('aria-disabled', 'true');
      }
    }
  }

  /** @param {string} fam @param {HTMLButtonElement} btn */
  async #addCross(fam, btn) {
    const status = this.#same?.querySelector('[data-status]');
    const say = (/** @type {string} */ s) => {
      if (status) status.textContent = s;
    };
    const U = /** @type {any} */ (window).TMFUpsell;
    const label = (U.NAMES && U.NAMES[fam]) || fam;
    const src = this.#current();
    if (!this.conf.cross?.[fam]) return;
    if (!src || !src.valid) {
      say('Finish your design above first, then add matching gifts.');
      return;
    }
    if (this.#busy) return;
    const vf = this.#variantFor(fam, src);
    if (!vf.variant) {
      const sel = /** @type {HTMLSelectElement | null} */ (btn.closest('.tmf-u__tile')?.querySelector('[data-opt]') || null);
      if (sel) {
        sel.setAttribute('aria-invalid', 'true');
        sel.focus();
      }
      say(`${label}: choose your ${fam === 'phone' ? 'phone model' : 'size'} first.`);
      return;
    }
    const TMF = /** @type {any} */ (window).TMF;
    const x = this.#cross(src, fam, fam === 'mug' ? undefined : vf.variant.title);
    const res = TMF.validateCfg(x.cfg, x.design, src.lib);
    if (!res.ok) {
      say(`${label}: ${(res.errors[0] || {}).msg || 'cannot use this design'}`);
      return;
    }
    const properties = { ...TMF.summary(res.cfg, x.design, src.lib), _cfg: JSON.stringify(res.cfg), _kind: fam };
    this.#busy = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      await cartAdd([{ id: Number(vf.variant.id), quantity: 1, properties }], this, 'tmf-upsell-pdp');
      btn.classList.add('is-added');
      btn.textContent = '✓ Added';
      say(`${label} (${vf.variant.title}) added to your cart · ${money(vf.variant.price, this.conf.moneyFormat)}.`);
    } catch (error) {
      say(/** @type {any} */ (error)?.message || 'Something went wrong. Please try again.');
    } finally {
      this.#busy = false;
      btn.removeAttribute('aria-busy');
    }
  }

  /* ---------- hoodie upgrade card ---------- */
  #variantId() {
    const form = /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId || ''));
    const input = /** @type {HTMLInputElement | null} */ (form?.querySelector('[name="id"]'));
    return Number(input?.value) || 0;
  }

  /** @param {any} src */
  #renderHoodie(src) {
    const box = /** @type {HTMLElement | null} */ (this.querySelector('[data-hoodie]'));
    if (!box) return;
    const c = this.conf;
    if (c.showHoodie === false || src.family !== 'apparel') {
      box.hidden = true;
      return;
    }
    const si = Number(c.styleIndex) || 0;
    const variants = /** @type {any[]} */ (c.variants || []);
    const cur = variants.find((v) => v.id === this.#variantId()) || variants[0];
    /** @type {any} */
    let target = null;
    if (si > 0 && cur) {
      if (/hood/i.test(cur.options[si - 1] || '')) {
        box.hidden = true; // already a hoodie
        return;
      }
      target = variants.find((v) => /hood/i.test(v.options[si - 1] || '') && v.options.every((o, i) => i === si - 1 || o === cur.options[i]));
    }
    if (!target && !c.showUnavailable) {
      box.hidden = true;
      return;
    }
    box.hidden = false;
    if (!box.firstChild) {
      const cv = el('canvas', { 'aria-hidden': 'true' });
      const btn = el('button', { type: 'button', cls: 'tmf-hoodie__btn', 'data-hoodie-btn': '' });
      btn.addEventListener('click', () => this.#pickHoodie());
      box.append(
        el('div', { cls: 'tmf-hoodie' }, [
          cv,
          el('div', { cls: 'tmf-hoodie__txt' }, [
            el('p', { cls: 'tmf-hoodie__k', text: 'Warmer gift' }),
            el('p', { cls: 'tmf-hoodie__t', text: 'Make it a hoodie' }),
            el('p', { cls: 'tmf-hoodie__d', 'data-hoodie-d': '', text: 'Same design on a heavy blend hoodie. Preview switches right away.' }),
          ]),
          btn,
        ])
      );
    }
    const btn = /** @type {HTMLButtonElement} */ (box.querySelector('[data-hoodie-btn]'));
    if (target) {
      const diff = target.price - (cur?.price || 0);
      btn.textContent = diff > 0 ? `Switch · +${money(diff, c.moneyFormat).replace('.00', '')}` : 'Switch to hoodie';
      btn.disabled = !target.available;
      btn.removeAttribute('aria-disabled');
    } else {
      btn.textContent = c.text?.soon || 'Coming soon';
      btn.setAttribute('aria-disabled', 'true');
    }
    const cv = /** @type {HTMLCanvasElement} */ (box.querySelector('canvas'));
    paintDesign(cv, cv.clientWidth || 72, {
      kind: 'apparel', design: src.design, cfg: src.cfg, lib: src.lib, media: this.media, mediaBase: this.mediaBase,
      colorName: src.colorName, colorHex: src.hex, style: 'Hoodie', onReady: this.#schedule,
    });
  }

  #pickHoodie() {
    const si = Number(this.conf.styleIndex) || 0;
    if (!si) return;
    const picker = this.#section.querySelector('variant-picker, variant-picker-component, [data-variant-picker]') || this.#section;
    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (picker.querySelectorAll('input[type="radio"]'));
    const hit = Array.from(inputs).find((i) => /hood/i.test(i.value));
    if (hit) {
      hit.click();
      return;
    }
    for (const s of /** @type {NodeListOf<HTMLSelectElement>} */ (picker.querySelectorAll('select'))) {
      const o = Array.from(s.options).find((x) => /hood/i.test(x.value));
      if (o) {
        s.value = o.value;
        s.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
  }

  /* ---------- add-ons ---------- */
  #buildAddons() {
    const host = this.querySelector('[data-addons]');
    if (!host) return;
    const c = this.conf, addons = c.addons || {};
    if (c.showAddons === false) return; // v4: "Make it a gift" switched off in the block settings
    const keys = /** @type {(keyof ADDON)[]} */ (['box', 'note', 'priority']).filter((k) => addons[k] || c.showUnavailable);
    if (!keys.length) return;
    const fs = el('fieldset', { cls: 'tmf-addons' }, [el('legend', { text: c.text?.addonsHeading || 'Make it a gift (optional)' })]);
    for (const k of keys) {
      const a = ADDON[k], info = addons[k], id = `${this.id}-addon-${k}`;
      const cb = /** @type {HTMLInputElement} */ (el('input', { type: 'checkbox', id, 'data-addon': k, 'aria-describedby': `${id}-sub`, disabled: !info }));
      const row = el('div', { cls: `tmf-addon${info ? '' : ' is-soon'}` }, [
        cb,
        el('label', { for: id, text: a.label }),
        el('span', { cls: 'tmf-addon__price', text: info ? `+${money(info.price, c.moneyFormat)}` : c.text?.soon || 'Coming soon' }),
        el('p', { cls: 'tmf-addon__sub', id: `${id}-sub`, text: a.sub }),
      ]);
      if (k === 'note') {
        const ta = /** @type {HTMLTextAreaElement} */ (el('textarea', { maxlength: '150', rows: '3', placeholder: 'Merry Christmas, Dad! Keep on rocking.', 'aria-label': 'Gift note message (150 characters max)', 'data-note': '' }));
        const cnt = el('span', { cls: 'tmf-addon__count', 'aria-live': 'polite', text: '0/150' });
        const wrap = el('div', { cls: 'tmf-addon__note', hidden: true }, [ta, cnt]);
        cb.addEventListener('change', () => {
          wrap.hidden = !cb.checked;
          if (cb.checked) ta.focus();
        });
        ta.addEventListener('input', () => {
          const U = /** @type {any} */ (window).TMFUpsell;
          const clean = U ? U.cleanNote(ta.value) : ta.value.slice(0, 150);
          if (clean !== ta.value) ta.value = clean;
          cnt.textContent = `${clean.length}/150`;
        });
        row.append(wrap);
      }
      fs.append(row);
    }
    host.append(fs);
    this.#updateEta();
  }

  #updateEta() {
    const sub = this.querySelector(`[id="${this.id}-addon-priority-sub"]`);
    const U = /** @type {any} */ (window).TMFUpsell;
    if (!sub) return;
    if (!U) {
      setTimeout(() => this.#updateEta(), 300);
      return;
    }
    const md = String(this.conf.christmasCutoff || '12-07').split('-').map(Number);
    const rules = { ...U.RULES, christmasCutoff: md.length === 2 ? md : U.RULES.christmasCutoff };
    const t = new Date(), a = U.deliveryEstimate(t, false), b = U.deliveryEstimate(t, true);
    sub.textContent =
      `Made in 1 business day. Arrives ${U.fmtDate(b.from)}–${U.fmtDate(b.to)} (standard ${U.fmtDate(a.from)}–${U.fmtDate(a.to)}). ` +
      (U.christmasOk(t, rules) ? 'Order by Dec 7 for Christmas.' : 'Christmas delivery not guaranteed after Dec 7.');
  }

  /** Main product added by the product form → add ticked add-ons; editing from the cart → remove the old line. */
  /** @param {any} event */
  #onCart = (event) => {
    const context = event?.context ?? event?.detail?.context;
    if (context && context !== 'product') return;
    if (event?.target instanceof Element && event.target.closest('tmf-upsell-pdp, .tmf-up, tmf-mug-addon-component')) return;
    const picked = /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[data-addon]:checked:not(:disabled)')));
    const editKey = this.#editKey;
    if (!picked.length && !editKey) return;
    const form = /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId || ''));
    let name = '';
    if (form) for (const [k, v] of new FormData(form).entries()) if (k === 'properties[Name]' && typeof v === 'string') name = v;
    const note = /** @type {HTMLTextAreaElement | null} */ (this.querySelector('[data-note]'))?.value || '';
    const promise = event?.promise;
    if (!promise || typeof promise.then !== 'function') return;
    promise.then(
      async (/** @type {any} */ result) => {
        if (result?.detail?.didError) return;
        try {
          if (picked.length) await this.#addAddons(picked, name, note);
          if (editKey) {
            this.#editKey = null;
            await this.#replaceEdited(editKey);
            const msg = /** @type {HTMLElement | null} */ (this.querySelector('[data-editing]'));
            if (msg) msg.textContent = 'Cart item updated with your new design.';
          }
        } catch (error) {
          console.warn('[tmf-upsell] after add', error);
        }
      },
      () => {}
    );
  };

  /** @param {HTMLInputElement[]} picked @param {string} name @param {string} note */
  async #addAddons(picked, name, note) {
    const addons = this.conf.addons || {};
    const cart = await getCart();
    const inCart = new Set((cart.items || []).map((/** @type {any} */ i) => Number(i.variant_id)));
    /** @type {{id: number, quantity: number, properties: Record<string, string>}[]} */
    const items = [];
    for (const cb of picked) {
      const k = cb.dataset.addon || '', info = addons[k];
      if (!info) continue;
      if (k === 'priority' && inCart.has(Number(info.variant))) continue; // once per order
      /** @type {Record<string, string>} */
      const properties = {};
      if (name) properties._for = name;
      if (k === 'note') {
        const U = /** @type {any} */ (window).TMFUpsell;
        const clean = U ? U.cleanNote(note) : note.slice(0, 150);
        if (clean.trim()) properties['Gift note'] = clean.trim();
      }
      items.push({ id: Number(info.variant), quantity: 1, properties });
    }
    if (!items.length) return;
    await cartAdd(items, this, 'tmf-upsell-pdp');
    for (const cb of picked) cb.checked = false;
    const ta = /** @type {HTMLTextAreaElement | null} */ (this.querySelector('[data-note]'));
    if (ta) {
      ta.value = '';
      const wrap = /** @type {HTMLElement | null} */ (ta.closest('.tmf-addon__note'));
      if (wrap) wrap.hidden = true;
    }
  }

  /** Removes the cart line that was opened with "Edit design" (found by key, else by variant + old _cfg). */
  /** @param {string} key */
  async #replaceEdited(key) {
    const cart = await getCart();
    const items = /** @type {any[]} */ (cart.items || []);
    const variant = Number(String(key).split(':')[0]) || 0;
    let line = items.find((i) => i.key === key);
    if (!line && this.#editCfg) line = items.find((i) => Number(i.variant_id) === variant && i.properties && i.properties._cfg === this.#editCfg);
    if (!line) return;
    // same design added again unchanged -> Shopify merged it into the old line: keep one
    const q = Number(line.quantity) || 1;
    await cartChange(line.key, q > 1 && line.properties?._cfg === this.#lastAddedCfg() ? q - 1 : 0, this, 'tmf-upsell-pdp');
  }

  #lastAddedCfg() {
    const form = /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId || ''));
    if (!form) return null;
    for (const [k, v] of new FormData(form).entries()) if (k === 'properties[_cfg]' && typeof v === 'string') return v;
    return null;
  }

  /* ---------- Edit design from the cart ---------- */
  #readEditLine() {
    try {
      const key = new URL(window.location.href).searchParams.get('tmf_line');
      if (!key || !/^[\w:]+$/.test(key)) return;
      this.#editKey = key;
      const TMF = /** @type {any} */ (window).TMF;
      const raw = new URL(window.location.href).searchParams.get('cfg');
      const dec = raw && TMF ? TMF.decodeCfg(raw) : null;
      if (dec) this.#editCfg = JSON.stringify(dec);
      else if (raw && !TMF && this.#editTries++ < 30) setTimeout(() => this.#readEditLine(), 400); // tmf-core not loaded yet
      const msg = /** @type {HTMLElement | null} */ (this.querySelector('[data-editing]'));
      if (msg) {
        msg.hidden = false;
        msg.textContent = 'Editing an item from your cart. Add to cart saves this design and replaces the old one.';
      }
    } catch {
      /* ignore */
    }
  }
}

if (!customElements.get('tmf-upsell-pdp')) customElements.define('tmf-upsell-pdp', TmfUpsellPdp);
